/**
 * Registry of languages lingua supports.
 *
 * Backed by `docs/roadmap.md`'s Phase 1F slicing in
 * `docs/multilingual-plan.md`. `available: true` only for languages
 * that have seeded content; everything else lists in the picker but
 * routes to the unseeded-language landing.
 *
 * German is the only one with a hard deadline — phase 1 is the visa
 * track. Other languages are open-ended.
 */

export type LanguageCode = "de" | "it" | "pt" | "el" | "la" | "tr";

export type LanguageMeta = {
  code: LanguageCode;
  nativeName: string;
  englishName: string;
  /** Spanish name — Jorge's L1, useful as a secondary label. */
  spanishName: string;
  /** Single-character marker. Country flags are politically loaded
   *  for some languages (Greek/Latin) so we use a glyph hint instead
   *  of an emoji flag and let the UI dress it up later. */
  glyph: string;
  /** Short hero line, used on home page. */
  tagline: string;
  /** Visa/exam deadline. Only German has one in phase 1. */
  deadline?: {
    /** ISO date `YYYY-MM-DD`. */
    date: string;
    /** Human reason shown next to the countdown. */
    reason: string;
  };
  /** True when there's seeded content for this language. UI uses
   *  this to gate normal pages vs. an empty-state landing. */
  available: boolean;
};

export const LANGUAGES: readonly LanguageMeta[] = [
  {
    code: "de",
    nativeName: "Deutsch",
    englishName: "German",
    spanishName: "Alemán",
    glyph: "DE",
    tagline:
      "Adaptive German learning for the B1 visa exam. Continuous memory modelling, spaced repetition, and exercises that target your actual weak points.",
    deadline: { date: "2026-11-30", reason: "Goethe B1 visa exam" },
    available: true,
  },
  {
    code: "it",
    nativeName: "Italiano",
    englishName: "Italian",
    spanishName: "Italiano",
    glyph: "IT",
    tagline:
      "Adaptive Italian learning, leveraging Spanish-Italian cognates and the false-friend traps that come with them.",
    available: false,
  },
  {
    code: "pt",
    nativeName: "Português",
    englishName: "Portuguese",
    spanishName: "Portugués",
    glyph: "PT",
    tagline:
      "Adaptive Portuguese learning, focused on the systematic Spanish-Portuguese divergences (false friends, nasal vowels, ser/estar nuances).",
    available: false,
  },
  {
    code: "el",
    nativeName: "Ελληνικά",
    englishName: "Modern Greek",
    spanishName: "Griego moderno",
    glyph: "EL",
    tagline: "Adaptive Modern Greek learning. Alphabet, declensions, verbs.",
    available: false,
  },
  {
    code: "la",
    nativeName: "Latina",
    englishName: "Latin",
    spanishName: "Latín",
    glyph: "LA",
    tagline: "Latin in reader mode — translation, glossing, no audio.",
    available: false,
  },
  {
    code: "tr",
    nativeName: "Türkçe",
    englishName: "Turkish",
    spanishName: "Turco",
    glyph: "TR",
    tagline:
      "Adaptive Turkish learning. Agglutinative morphology, vowel harmony, no grammatical gender.",
    available: false,
  },
] as const;

const BY_CODE: ReadonlyMap<LanguageCode, LanguageMeta> = new Map(
  LANGUAGES.map((l) => [l.code, l]),
);

/** Lookup by code. Returns `undefined` for unknown codes — callers
 *  decide whether to fall back to the default or treat as error. */
export function getLanguage(code: string): LanguageMeta | undefined {
  return BY_CODE.get(code as LanguageCode);
}

export function isLanguageCode(code: string): code is LanguageCode {
  return BY_CODE.has(code as LanguageCode);
}

export const DEFAULT_LANGUAGE: LanguageCode = "de";
